import React, { useCallback, useMemo } from 'react'
import MethodMappingForm from './Components/MethodMappingForm'
import MethodMappingList from './Components/MethodMappingList'
import MappingHeader from '../Components/MappingHeader'
import StatusBanner from '../Components/StatusBanner'
import useMethodMapping, { isRowComplete } from './useMethodMapping'
import Shared from '../mappingUi.module.css'

/**
 * Payment and shipping mappings are the same screen over the same payload shape,
 * so both tabs render this and differ only by `resource` (the endpoint family) and
 * the labels. See PaymentMapping.jsx / ShippingMapping.jsx for the two call sites.
 */
const MethodMapping = ({ resource, title, entityLabel, emptyIcon, idPrefix, connectorId, instancesId }) => {
    const {
        erpMethods,
        ecommerceMethods,
        rows,
        isLoading,
        loadError,
        isSaving,
        saveStatus,
        addRow,
        updateRow,
        removeRow,
        submitMappings,
        dismissStatus
    } = useMethodMapping(resource, entityLabel, connectorId, instancesId)

    const completeRowCount = useMemo(() => rows.filter(isRowComplete).length, [rows])
    const hasIncompleteRow = completeRowCount !== rows.length

    const isDuplicatePair = useCallback(
        (erpMethod, ecommerceMethod) => rows.some(
            (row) => row.erpMethod === erpMethod && row.ecommerceMethod === ecommerceMethod
        ),
        [rows]
    )

    const isBusy = isLoading || isSaving

    return (
        <>
            <MappingHeader
                title={title}
                countLabel={!isLoading && !loadError ? `${completeRowCount} mapped` : ''}
                isSaving={isSaving}
                isDisabled={isLoading || Boolean(loadError)}
                onSubmit={submitMappings}
            />

            <div className={`card-body p-10 ${Shared.body}`}>
                <StatusBanner status={saveStatus} onDismiss={dismissStatus} />

                <MethodMappingForm
                    idPrefix={idPrefix}
                    entityLabel={entityLabel}
                    erpMethods={erpMethods}
                    ecommerceMethods={ecommerceMethods}
                    isDisabled={isBusy || Boolean(loadError)}
                    isDuplicate={isDuplicatePair}
                    onAdd={addRow}
                />

                {hasIncompleteRow ? (
                    <p className={Shared.hint}>
                        <i className="uil-info-circle" aria-hidden="true" />
                        {' '}Rows with an empty method are skipped when you submit.
                    </p>
                ) : null}

                <MethodMappingList
                    rows={rows}
                    idPrefix={idPrefix}
                    entityLabel={entityLabel}
                    emptyIcon={emptyIcon}
                    erpMethods={erpMethods}
                    ecommerceMethods={ecommerceMethods}
                    isLoading={isLoading}
                    loadError={loadError}
                    isDisabled={isSaving}
                    onChange={updateRow}
                    onRemove={removeRow}
                />
            </div>
        </>
    )
}

export default MethodMapping
